import { useState } from 'react';
import { C } from '../../../lib/tokens.js';
import { t } from '../../../lib/data.js';
import { useLookups } from '../../../lib/LookupsContext.jsx';
import { useViewport } from '../../../lib/useViewport.js';
import { FilterPill } from '../../../components/ui/FilterPill.jsx';
import { useExtensionSlot } from '../../../lib/ExtensionSlotsContext.jsx';

// `trailing` (desktop only) is rendered at the right end of the bar, e.g. the board's sort.
export function RequestFilters({ filters, setFilters, role, view, trailing }) {
  const { statuses, requestTypes, bpartners } = useLookups();
  const { isMobile } = useViewport();
  const [sheetOpen, setSheetOpen] = useState(false);
  const update = (k, v) => setFilters({ ...filters, [k]: v });
  // undefined when afsp.afts isn't installed - no cluster concept to filter by then.
  const ClusterFilter = useExtensionSlot('request.filter.clusterPicker');

  const activeCount = [
    view !== 'board' && !!filters.status,
    !!filters.type,
    role === 'agent' && !!filters.bpartnerId,
    role === 'agent' && !!filters.clusterId,
    role === 'agent' ? !!filters.scope && filters.scope !== 'all' : !!filters.scope && filters.scope !== 'mine',
  ].filter(Boolean).length;

  const scopePill = role === 'agent' ? (
    <FilterPill fullWidth={isMobile} label={t('req.filter.scope')} value={filters.scope || 'all'} icon="filter_alt" onChange={v => update('scope', v)} options={[
      { id: 'all',  name: t('req.filter.scope.all') }, { id: 'mine', name: t('req.filter.scope.assigned_to_me') },
      { id: 'created', name: t('req.filter.scope.created_by_me') }, { id: 'waiting', name: t('req.filter.scope.waiting_for_me') },
    ]} />
  ) : (
    <FilterPill fullWidth={isMobile} label={t('req.filter.scope')} value={filters.scope || 'mine'} icon="groups" onChange={v => update('scope', v)} options={[
      { id: 'mine', name: t('req.filter.scope.mine') }, { id: 'company', name: t('req.filter.company') },
    ]} />
  );

  // On mobile the scope pill leads the filter sheet - it is the filter people reach for first.
  const pills = (
    <>
      {isMobile && scopePill}
      {view !== 'board' ? (
        <FilterPill fullWidth={isMobile} label={t('req.filter.status')} value={filters.status} options={[{ id: '', name: t('req.filter.all') }, ...statuses]} onChange={v => update('status', v)} icon="flag" />
      ) : null}
      <FilterPill fullWidth={isMobile} label={t('req.filter.type')}   value={filters.type}   options={[{ id: '', name: t('req.filter.all') }, ...requestTypes]} onChange={v => update('type', v)} icon="category" />
      {/* Date filter temporarily disabled — re-enable when ready.
      <FilterPill label={t('req.filter.date')}   value={filters.range}  icon="event" onChange={v => update('range', v)} options={[
        { id: '', name: t('req.filter.range.any') }, { id: '7', name: t('req.filter.range.7d') },
        { id: '30', name: t('req.filter.range.30d') }, { id: '90', name: t('req.filter.range.90d') },
      ]} />
      */}
      {role === 'agent' ? (
        <FilterPill fullWidth={isMobile} label={t('req.filter.bpartner')} value={filters.bpartnerId} icon="apartment" onChange={v => update('bpartnerId', v)} options={[{ id: '', name: t('req.filter.all') }, ...bpartners]} searchable
          searchPlaceholder={t('req.filter.type_to_search')} noMatchesLabel={t('req.filter.no_matches')} />
      ) : null}
      {role === 'agent' && ClusterFilter ? (
        <ClusterFilter value={filters.clusterId} onChange={v => update('clusterId', v)} fullWidth={isMobile} />
      ) : null}

      {!isMobile && scopePill}
    </>
  );

  const searchBox = (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 4,
      border: `1px solid ${C.mercury}`, background: C.tin,
      ...(isMobile ? { flex: 1, minWidth: 0 } : { minWidth: 180, flex: '1 1 260px', maxWidth: 320 }),
    }}>
      <span className="material-icons" style={{ fontSize: 16, color: C.iron }}>search</span>
      <input value={filters.q || ''} onChange={e => update('q', e.target.value)}
        placeholder={t('req.filter.search_placeholder')}
        style={{ border: 'none', outline: 'none', fontSize: 13, flex: 1, fontFamily: 'Roboto', minWidth: 0 }} />
    </div>
  );

  if (isMobile) {
    // A horizontally-scrolling chip strip still reads as something to discover by
    // accident. A single "Filters" button (badge = active count) that opens a
    // bottom sheet keeps the visible bar to just search + one button, same spirit
    // as the icon-only view switcher.
    return (
      <div style={{ display: 'flex', gap: 8, padding: '10px 16px', background: C.tin, borderBottom: `1px solid ${C.mercury}` }}>
        {searchBox}
        <button type="button" onClick={() => setSheetOpen(true)} style={{
          position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 38, flexShrink: 0, borderRadius: 4, border: `1px solid ${activeCount ? C.liquid : C.mercury}`,
          background: activeCount ? C.rowActive : C.tin, cursor: 'pointer',
        }}>
          <span className="material-icons" style={{ fontSize: 18, color: activeCount ? C.liquid : C.iron }}>tune</span>
          {activeCount > 0 && (
            <span style={{
              position: 'absolute', top: -5, right: -5, minWidth: 16, height: 16, padding: '0 3px',
              borderRadius: 999, background: C.liquid, color: '#fff', fontSize: 9, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Roboto',
            }}>{activeCount}</span>
          )}
        </button>

        {sheetOpen && (
          <>
            <div onClick={() => setSheetOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 199 }} />
            <div style={{
              position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 200,
              background: C.tin, borderTopLeftRadius: 12, borderTopRightRadius: 12,
              maxHeight: '70vh', overflowY: 'auto', padding: 16,
              boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
                <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: C.deepsea }}>{t('req.filter.title')}</span>
                <button type="button" onClick={() => setSheetOpen(false)}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}>
                  <span className="material-icons" style={{ fontSize: 20, color: C.iron }}>close</span>
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {pills}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      padding: '12px 24px', background: C.tin, borderBottom: `1px solid ${C.mercury}`,
    }}>
      {searchBox}
      {pills}
      {trailing ? <><div style={{ flex: 1 }} />{trailing}</> : null}
    </div>
  );
}

// `finalClosedStatusIds`: statuses with R_Status.IsFinalClose = 'Y' (lookup `terminal`).
// Agent scope "waiting": requests assigned to me in one of these statuses (R_Status.Value).
export const WAITING_FOR_ME_STATUS_VALUES = ['Software development in progress', 'Waiting on customer response'];

// `waitingStatusIds`: ids of the WAITING_FOR_ME_STATUS_VALUES statuses.
export function applyFilters(rows, filters, role, meId, isSearch = false, finalClosedStatusIds = null, waitingStatusIds = null) {
  // SAFETY: Filter out any null or broken records instantly
  let out = (rows || []).filter(Boolean);
  // Status/type/priority/q/range are applied server-side. Scope is enforced
  // here too as a client-side safety net for both list and search results.
  if (role === 'agent'    && filters.scope === 'mine')              out = out.filter(r => r.assigneeId === meId);
  // "Assigned to me" means "my work that isn't done for good": hide permanently closed requests,
  // unless a status filter is set - picking a final-close status explicitly shows them.
  if (role === 'agent'    && filters.scope === 'mine' && !filters.status && finalClosedStatusIds?.size) out = out.filter(r => !finalClosedStatusIds.has(r.status));
  if (role === 'agent'    && filters.scope === 'created')           out = out.filter(r => r.createdById === meId);
  if (role === 'agent'    && filters.scope === 'waiting')           out = out.filter(r => r.assigneeId === meId && !!waitingStatusIds?.has(r.status));
  if (role === 'customer' && (filters.scope === 'mine' || !filters.scope)) out = out.filter(r => r.reporterId === meId);
  return out;
}